  // E:\tamillearning.ai UI\Tamil-Katral-Maiyam\src\pages\student\Conversation.tsx

  import React from "react";
  import { Layout } from "../../components/layout/Layout";
  import { PageHeader } from "../../components/layout/PageHeader";
  import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
  } from "../../components/ui/Card";
  import {
    MessageCircle,
    Bot,
    Users,
    Sparkles,
    Play,
    Volume2,
  } from "lucide-react";

  export const ConversationPage: React.FC = () => {
    const IMAGES = {
      teacher: "/images/malarteacher.jpeg",
      speak: "/images/tamilspeak.jpeg",
      friends: "/images/3_friends.jpeg",
    };

    // ✅ Card shell
    const cardShell =
      "p-0 overflow-hidden rounded-3xl border-4 border-white/30 shadow-2xl hover:scale-[1.02] transition-all duration-300";

    // ✅ Fixed frame for perfect crop
    const frame = "relative h-[360px] w-full overflow-hidden rounded-3xl";

    // ✅ Light+Dark readability overlay (very light, image stays visible)
    const scrim =
      "absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/18";

    /**
     * ✅ TEXT: add subtle white glow highlight INSIDE the chip (so text pops)
     * - keep small black shadow for readability
     * - glow is very light (not neon)
     */
    const titleText =
      "text-white text-2xl font-black drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)] [text-shadow:0_0_10px_rgba(255,255,255,0.22)]";
    const bigText =
      "text-white text-3xl font-extrabold drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)] [text-shadow:0_0_12px_rgba(255,255,255,0.22)]";
    const labelText =
      "text-white font-semibold drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] [text-shadow:0_0_8px_rgba(255,255,255,0.18)]";

    /**
     * ✅ CHIPS: transparent black + blur + tiny shadow (no heavy black box)
     * - shadow reduced so it looks clean
     */
    const chip =
      "bg-black/28 backdrop-blur-sm px-4 py-2 rounded-2xl border border-white/18 shadow-[0_1px_3px_rgba(0,0,0,0.18)]";

    const iconChip =
      "p-3 bg-black/26 backdrop-blur-sm rounded-2xl border border-white/18 shadow-[0_1px_3px_rgba(0,0,0,0.16)]";

    const pill =
      "bg-black/24 backdrop-blur-sm px-4 py-2 rounded-xl inline-block border border-white/18 shadow-[0_1px_3px_rgba(0,0,0,0.16)]";

    const primaryBtn =
      "w-full bg-black/22 backdrop-blur-sm hover:bg-black/28 border border-white/18 text-white font-extrabold py-3 px-6 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.18)] hover:scale-[1.02] transition-all flex items-center justify-center gap-2";

    const friendBtn =
      "bg-black/20 backdrop-blur-sm hover:bg-black/26 border border-white/18 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-[0_1px_3px_rgba(0,0,0,0.16)]";

    // ✅ IMPORTANT: Hide kids/characters by repositioning background
    const imgBase = "absolute inset-0 h-full w-full object-cover";
    const posTeacher = "object-[70%_50%]";
    const posSpeak = "object-[65%_50%]";
    const posFriends = "object-[50%_35%]";

    return (
      <Layout>
        <PageHeader
          title="🎤 Chat & Learn Fun! 🎤"
          subtitle="Talk with AI friends and practice Tamil! 🗣️✨"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* 1) AI Chat Bot */}
          <Card className={cardShell}>
            <div className={frame}>
              <img
                src={IMAGES.teacher}
                alt="AI Malar Teacher"
                className={`${imgBase} ${posTeacher}`}
                draggable={false}
              />
              <div className={scrim} />

              <div className="relative z-10 p-6">
                <CardHeader className="p-0 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={iconChip}>
                      <Bot className="w-10 h-10 text-yellow-300" />
                    </div>
                    <div className={chip}>
                      <CardTitle className={titleText}>
                        AI Malar Teacher 🤖
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-0 mt-6">
                  <div className={`${chip} inline-block`}>
                    <p className={bigText}>24/7</p>
                  </div>

                  <div className="mt-3">
                    <span className={pill}>
                      <span className={labelText}>
                        Always Ready to Chat! 💬
                      </span>
                    </span>
                  </div>

                  <ul className="mt-4 space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                      <span className={labelText}>Tamil stories & jokes</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                      <span className={labelText}>Homework help</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                      <span className={labelText}>Pronunciation practice</span>
                    </li>
                  </ul>
                </CardContent>
              </div>
            </div>
          </Card>

          {/* 2) Speak Tamil */}
          <Card className={cardShell}>
            <div className={frame}>
              <img
                src={IMAGES.speak}
                alt="Speak Tamil"
                className={`${imgBase} ${posSpeak}`}
                draggable={false}
              />
              <div className={scrim} />

              <div className="relative z-10 p-6">
                <CardHeader className="p-0 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={iconChip}>
                      <Volume2 className="w-10 h-10 text-orange-300" />
                    </div>
                    <div className={chip}>
                      <CardTitle className={titleText}>Speak Tamil 🎙️</CardTitle>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-0 mt-6">
                  <div className={`${chip} inline-block`}>
                    <p className={bigText}>95%</p>
                  </div>

                  <div className="mt-3">
                    <span className={pill}>
                      <span className={labelText}>Pronunciation Score</span>
                    </span>
                  </div>

                  <div className="mt-6 space-y-3">
                    <button className={primaryBtn}>
                      <Play className="w-6 h-6" />
                      Practice Now!
                    </button>

                    <p className="text-sm text-center">
                      <span className={labelText}>
                        Say "வணக்கம்" and get stars! ⭐
                      </span>
                    </p>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>

          {/* 3) Friends Chat */}
          <Card className={cardShell}>
            <div className={frame}>
              <img
                src={IMAGES.friends}
                alt="Friends Chat"
                className={`${imgBase} ${posFriends}`}
                draggable={false}
              />
              <div className={scrim} />

              <div className="relative z-10 p-6">
                <CardHeader className="p-0 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={iconChip}>
                      <Users className="w-10 h-10 text-yellow-300" />
                    </div>
                    <div className={chip}>
                      <CardTitle className={titleText}>Friends Chat 👥</CardTitle>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-0 mt-6">
                  <div className={`${chip} inline-block`}>
                    <p className={bigText}>23</p>
                  </div>

                  <div className="mt-3">
                    <span className={pill}>
                      <span className={labelText}>Classmates Online</span>
                    </span>
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <button className={friendBtn}>
                      <div className="text-sm">அனு</div>
                      <div className="text-xs text-yellow-200">Online</div>
                    </button>
                    <button className={friendBtn}>
                      <div className="text-sm">ராம்</div>
                      <div className="text-xs text-yellow-200">Online</div>
                    </button>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Practice Section (kept) */}
        <div className="relative overflow-hidden rounded-3xl shadow-2xl border-4 border-white/25 max-w-4xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500" />
          <div className="absolute inset-0 bg-black/12" />

          <div className="relative z-10 text-white p-8 text-center">
            <MessageCircle className="w-20 h-20 mx-auto mb-6 text-yellow-300" />
            <h2 className="text-4xl font-black mb-4 text-white drop-shadow-[0_2px_3px_rgba(0,0,0,0.35)]">
              Ready to Chat? 🚀
            </h2>
            <p className="text-xl mb-8 font-semibold max-w-2xl mx-auto text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.28)]">
              Practice Tamil with AI friends, record your voice, and chat with
              classmates! Every conversation makes you better! 🌟
            </p>
          </div>
        </div>
      </Layout>
    );
  };

  export default ConversationPage;
